import { useForm } from "react-hook-form";
import {
  useGetAllOrganizationsQuery,
  useGetOrganizationProjectsQuery,
} from "src/features/organizations/organization.service";
import { CreateCreditVouchers, createCreditVouchersSchema } from "../../types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "src/components/Form/button";
import { useCreateCreditVoucherMutation } from "../../credit-voucher.service";
import CreatedVouchersModal from "./created-vouchers-modal";
import { VouchersFormFields } from "./vouchers-form-fields";
import { Toast } from "src/components/toast/Toast";
import { useEffect, useState, useMemo } from "react";
import { DownloadVouchersCodesPdf } from "../../utils";
import { useAppDispatch } from "src/store";
import { showToast } from "src/store/toast.store";
import Swal from "sweetalert2";
import "./sweetalert-custom.css";

export const CreateVouchersForm = () => {
  const [createdVouchersPdfInfo, setCreatedVoucherPdfInfo] = useState<DownloadVouchersCodesPdf["vouchersInfo"]>();
  const { data: organizations } = useGetAllOrganizationsQuery(20);
  const dispatch = useAppDispatch();
  const [createCreditVouchers, { data: createdVouchers, error: createVoucherError, isLoading }] =
    useCreateCreditVoucherMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CreateCreditVouchers>({
    resolver: zodResolver(createCreditVouchersSchema),
    defaultValues: {
      type: "PROMO",
      currency: "KARMA",
      count: 1,
      amount: 0,
      expiryDate: undefined,
    },
  });

  const organizationId = watch("organizationId");
  const projectId = watch("projectId");
  const voucherType = watch("type") as CreateCreditVouchers["type"];

  const { data: orgProjects } = useGetOrganizationProjectsQuery(organizationId!, { skip: !organizationId });

  const selectedOrgProjects = useMemo(() => {
    if (!organizationId) return [];
    const allProjects = orgProjects ?? [];
    const now = new Date();
    
    // Filter projects: active (not deactivated), end date hasn't passed, and type is VOUCHER or VOUCHERS-HOURS-REGISTRATIONS (if PREMIUM types)
    // Otherwise, show all projects (for non-PREMIUM types)
    return allProjects.filter(project => {
      const isActive = !project.deactivated;
      const endDate = project.dateRange?.end?.toDate?.() || project.dateRange?.end;
      const hasNotEnded = endDate && new Date(endDate) >= now;
      
      if (voucherType === "PREMIUM" || voucherType === "MONTHLY_PREMIUM" || voucherType === "YEARLY_PREMIUM") {
        const isCorrectType = project.projectType === "VOUCHER" || project.projectType === "VOUCHERS-HOURS-REGISTRATIONS";
        return isActive && hasNotEnded && isCorrectType;
      }
      
      return isActive && hasNotEnded;
    });
  }, [organizationId, orgProjects, voucherType]);

  const useProjectEndDate = watch("useProjectEndDate");

  // Check if selected project is PRIVATE_MODE or PRIVATE_MODE_TOP_UP and set amount to 0, currency to KARMA, and type to PRIVATE_MODE
  useEffect(() => {
    const isPremiumType = voucherType === "PREMIUM" || voucherType === "MONTHLY_PREMIUM" || voucherType === "YEARLY_PREMIUM";
    
    if (isPremiumType) {
      // For PREMIUM types, always set amount to 0 and currency to KARMA
      setValue("amount", 0);
      setValue("currency", "KARMA");
      
      // Clear project if it doesn't match PREMIUM requirements (VOUCHER or VOUCHERS-HOURS-REGISTRATIONS)
      if (projectId && selectedOrgProjects.length > 0) {
        const selectedProject = selectedOrgProjects.find(project => project.id === projectId);
        if (selectedProject && selectedProject.projectType !== "VOUCHER" && selectedProject.projectType !== "VOUCHERS-HOURS-REGISTRATIONS") {
          setValue("projectId", undefined);
        } else if (selectedProject?.dateRange?.end && voucherType === "PREMIUM") {
          // If checkbox is checked, set subscriptionEndDate to project's end date
          if (useProjectEndDate) {
            const endDate = selectedProject.dateRange.end.toDate?.() || selectedProject.dateRange.end;
            const dateString = endDate instanceof Date ? endDate.toISOString().split('T')[0] : new Date(endDate).toISOString().split('T')[0];
            setValue("subscriptionEndDate", dateString);
          } else if (useProjectEndDate === false) {
            // If checkbox is explicitly unchecked, clear the subscriptionEndDate
            setValue("subscriptionEndDate", undefined);
          }
        }
      } else if (!projectId) {
        // If project is cleared, clear subscriptionEndDate and checkbox
        setValue("subscriptionEndDate", undefined);
        setValue("useProjectEndDate", false);
      }
      return;
    }
    
    if (projectId && selectedOrgProjects.length > 0) {
      const selectedProject = selectedOrgProjects.find(project => project.id === projectId);
      if (selectedProject?.projectType === "PRIVATE_MODE" || selectedProject?.projectType === "PRIVATE_MODE_TOP_UP") {
        setValue("amount", 0);
        setValue("currency", "KARMA");
        setValue("type", "PRIVATE_MODE");
      }
    }
  }, [projectId, selectedOrgProjects, setValue, voucherType, useProjectEndDate]);

  const onSubmit = async (data: CreateCreditVouchers) => {
    // Calculate subscription end date for display
    let displaySubscriptionEndDate = data.subscriptionEndDate;
    if (data.type === "PREMIUM" && data.useProjectEndDate && data.projectId && selectedOrgProjects.length > 0) {
      const selectedProject = selectedOrgProjects.find(project => project.id === data.projectId);
      if (selectedProject?.dateRange?.end) {
        const endDate = selectedProject.dateRange.end.toDate?.() || selectedProject.dateRange.end;
        displaySubscriptionEndDate = endDate instanceof Date ? endDate.toISOString().split('T')[0] : new Date(endDate).toISOString().split('T')[0];
      }
    }
    
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Create Credit Vouchers?',
      html: `
        <div class="text-left">
          <p class="mb-2"><strong>Type:</strong> ${data.type}</p>
          <p class="mb-2"><strong>Amount:</strong> ${data.amount} ${data.currency}</p>
          <p class="mb-2"><strong>Count:</strong> ${data.count}</p>
          ${data.expiryDate ? `<p class="mb-2"><strong>Expiry:</strong> ${new Date(data.expiryDate).toLocaleDateString()}</p>` : ''}
          ${data.type === "PREMIUM" && displaySubscriptionEndDate ? `<p class="mb-2"><strong>Subscription End Date:</strong> ${new Date(displaySubscriptionEndDate).toLocaleDateString()}${data.useProjectEndDate ? ' (from project)' : ''}</p>` : ''}
          ${data.type === "MONTHLY_PREMIUM" ? `<p class="mb-2"><strong>Type:</strong> Monthly Premium Subscription</p>` : ''}
          ${data.type === "YEARLY_PREMIUM" ? `<p class="mb-2"><strong>Type:</strong> Yearly Premium Subscription</p>` : ''}
          ${data.organizationId ? `<p class="mb-2"><strong>Organization:</strong> ${organizations?.find((org) => org.id === data.organizationId)?.name}</p>` : ''}
          ${data.projectId ? `<p class="mb-2"><strong>Project:</strong> ${selectedOrgProjects?.find((project) => project.id === data.projectId)?.name}</p>` : ''}
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Create Vouchers!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'swal2-confirm-custom',
        cancelButton: 'swal2-cancel-custom'
      }
    });

    if (result.isConfirmed) {
      // Show loading state
      Swal.fire({
        title: 'Creating Vouchers...',
        text: 'Please wait while we generate your credit vouchers',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // For PREMIUM types, ensure amount is 0 and subscriptionEndDate is included (only for PREMIUM, not MONTHLY_PREMIUM or YEARLY_PREMIUM)
        const isPremiumType = data.type === "PREMIUM" || data.type === "MONTHLY_PREMIUM" || data.type === "YEARLY_PREMIUM";
        
        // If PREMIUM type and checkbox is checked, get the project's end date
        let subscriptionEndDate = undefined;
        if (data.type === "PREMIUM") {
          if (data.useProjectEndDate && data.projectId && selectedOrgProjects.length > 0) {
            const selectedProject = selectedOrgProjects.find(project => project.id === data.projectId);
            if (selectedProject?.dateRange?.end) {
              const endDate = selectedProject.dateRange.end.toDate?.() || selectedProject.dateRange.end;
              subscriptionEndDate = endDate instanceof Date ? endDate.toISOString().split('T')[0] : new Date(endDate).toISOString().split('T')[0];
            }
          } else {
            subscriptionEndDate = data.subscriptionEndDate;
          }
        }
        
        const submitData = {
          ...data,
          amount: isPremiumType ? 0 : data.amount,
          subscriptionEndDate: subscriptionEndDate,
          useProjectEndDate: undefined, // Don't send checkbox value to backend
          linkToProjectTermination: data.linkToProjectTermination ? true : undefined, // Only send if checked
        };
        
        await createCreditVouchers(submitData).unwrap();
        
        setCreatedVoucherPdfInfo({
          type: data.type,
          amount: data.amount,
          count: data.count,
          currency: data.currency,
          expiryDate: data.expiryDate,
          organizationName: organizations?.find((org) => org.id === data.organizationId)?.name,
          projectName: orgProjects?.find((project) => project.id === data.projectId)?.name,
        });

        // Show success message
        Swal.fire({
          title: 'Success!',
          text: `${data.count} credit voucher${data.count > 1 ? 's' : ''} created successfully!`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'View Vouchers'
        });
      } catch (error) {
        // Show error message
        Swal.fire({
          title: 'Error!',
          text: error as string || 'Failed to create vouchers. Please try again.',
          icon: 'error',
          confirmButtonColor: '#ef4444',
          confirmButtonText: 'Try Again'
        });
      }
    }
  };

  useEffect(() => {
    if (createVoucherError) dispatch(showToast({ message: createVoucherError as string, type: "error" }));
  }, [createVoucherError, dispatch]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Credit Vouchers</h1>
          <p className="text-gray-600">Generate credit vouchers for your organization or project</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <VouchersFormFields
            errors={errors}
            register={register}
            organizations={organizations}
            projects={selectedOrgProjects}
            selectedProjectId={projectId}
            voucherType={voucherType}
            watch={watch}
            setValue={setValue}
          />
          
          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 pt-6">
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Vouchers
                </div>
              )}
            </Button>
          </div>
        </form>

        {/* Modals */}
        {createdVouchers && createdVouchersPdfInfo ? (
          <CreatedVouchersModal codes={createdVouchers.voucherCodes} vouchersInfo={createdVouchersPdfInfo} />
        ) : null}
        {createVoucherError ? (
          <Toast message={(createVoucherError as string) ?? "something went wrong"} type="error" />
        ) : null}
      </div>
    </div>
  );
};
